/**
 * Servicio de Patente que usa la nueva API backend
 * Siguiendo el mismo patrón que clientesApiService.js
 */

import { apiHelpers } from './normalizedApiClient';
import { normalizeDataObject } from '../utils/normalizationUtils';
import { API_ENDPOINTS } from '../config/api';
import { patenteTransforms } from '../utils/dataTransforms';

// Cache simple en memoria para consultas de patente con mapeo de IDs
const patenteCache = new Map();
const CACHE_TIMEOUT = 24 * 60 * 60 * 1000; // 24 horas

/**
 * Verificar cache de patente con IDs mapeados
 */
const getFromCache = (patente) => {
  const cacheKey = patente.toUpperCase();
  const cached = patenteCache.get(cacheKey);
  
  if (cached && (Date.now() - cached.timestamp) < CACHE_TIMEOUT) {
    //console.log(`🚗 [PatenteService] Cache hit para patente con IDs mapeados: ${patente}`);
    return cached.data;
  }
  
  // Limpiar cache expirado
  if (cached) {
    patenteCache.delete(cacheKey);
  }
  
  return null;
};

/**
 * Guardar en cache después del mapeo de IDs
 */
const saveToCache = (patente, data) => {
  if (!data || !data.success || !data.found) return;
  
  const cacheKey = patente.toUpperCase();
  patenteCache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });
  //console.log(`🚗 [PatenteService] Datos con IDs mapeados guardados en cache: ${patente}`);
};

/**
 * Consulta información de vehículo por patente
 * @param {string} patente - Patente a consultar
 * @param {string} method - Método a usar: 'auto', 'scraping', 'boostr'
 * @returns {Promise<Object>} - Resultado de la consulta
 */
export const consultarPatente = async (patente, method = 'auto') => {
  try {
    //console.log('🚗 [PatenteService] Consultando patente:', patente, 'método:', method);
    
    if (!patente || typeof patente !== 'string') {
      throw new Error('Patente es requerida');
    }

    // Limpiar y validar formato de patente
    const patenteClean = patente.toUpperCase().trim();
    
    // Verificar cache primero (después del mapeo de IDs)
    const cached = getFromCache(patenteClean);
    if (cached) {
      return cached;
    }
    
    // Usar cliente normalizado para hacer la consulta
    const response = await apiHelpers.generic.post(
      API_ENDPOINTS.PATENTES.CONSULTAR,
      { 
        patente: patenteClean,
        method: method 
      },
      'patente'
    );
    
    if (response?.data) {
      //console.log('✅ [PatenteService] Respuesta recibida:', response.data);
      
      // El normalizedApiClient ya maneja la normalización básica
      const resultado = response.data;
      
      // Usar transforms async para normalizar la respuesta y mapear IDs
      //console.log('🔄 [PatenteService] Datos antes de transform:', resultado);
      const consultaTransformada = await patenteTransforms.fromConsultaPatenteAsync(resultado);
      //console.log('🔄 [PatenteService] Datos después de transform:', consultaTransformada);
      
      if (consultaTransformada.success && consultaTransformada.found) {
        // Aplicar normalización adicional a los datos anidados
        if (consultaTransformada.cliente) {
          consultaTransformada.cliente = normalizeDataObject(consultaTransformada.cliente, 'cliente');
        }
        if (consultaTransformada.vehiculo) {
          consultaTransformada.vehiculo = normalizeDataObject(consultaTransformada.vehiculo, 'vehiculo');
        }
        
        // Guardar en cache después del mapeo de IDs
        saveToCache(patenteClean, consultaTransformada);
        
        return consultaTransformada;
      } else {
        // No se encontraron datos o falló la consulta
        return {
          ...consultaTransformada,
          patente: patenteClean,
          requiresManualInput: true
        };
      }
    }
    
    // Respuesta vacía o malformada
    throw new Error('Respuesta inválida del servidor');
    
  } catch (error) {
    console.error('❌ [PatenteService] Error consultando patente:', error);
    
    // Manejar diferentes tipos de errores
    let errorMessage = 'Error al consultar patente';
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
    } else if (error.message.includes('HTTP: 4') || error.status >= 400 && error.status < 500) {
      errorMessage = 'Error de validación. Verifica el formato de la patente.';
    } else if (error.message.includes('HTTP: 5') || error.status >= 500) {
      errorMessage = 'Error del servidor. Intenta nuevamente en unos momentos.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      found: false,
      success: false,
      error: errorMessage,
      requiresManualInput: true,
      patente: patente?.toUpperCase() || ''
    };
  }
};

/**
 * Crea cliente y vehículo a partir de datos de patente
 * @param {Object} clienteData - Datos del cliente
 * @param {Object} vehiculoData - Datos del vehículo
 * @returns {Promise<Object>} - Cliente y vehículo creados
 */
export const crearClienteVehiculo = async (clienteData, vehiculoData) => {
  try {
    //console.log('👥🚗 [PatenteService] Creando cliente y vehículo');
    //console.log('Cliente:', clienteData);
    //console.log('Vehículo:', vehiculoData);
    
    // Validar datos requeridos
    if (!clienteData?.nombre || !vehiculoData?.patente) {
      throw new Error('Nombre del cliente y patente del vehículo son requeridos');
    }
    
    // Usar transforms para preparar datos para la API
    const datosParaAPI = patenteTransforms.toCreateClienteVehiculo(clienteData, vehiculoData);
    
    // Usar cliente normalizado para crear
    const response = await apiHelpers.generic.post(
      API_ENDPOINTS.PATENTES.CREAR_CLIENTE_VEHICULO,
      datosParaAPI,
      'clienteVehiculo'
    );
    
    if (response?.data?.success) {
      //console.log('✅ [PatenteService] Cliente y vehículo creados exitosamente');
      
      const resultado = response.data;
      
      // Normalizar datos usando el sistema centralizado
      const clienteCreado = normalizeDataObject(resultado.data.cliente, 'cliente');
      const vehiculoCreado = normalizeDataObject(resultado.data.vehiculo, 'vehiculo');
      
      return {
        success: true,
        cliente: clienteCreado,
        vehiculo: vehiculoCreado,
        message: resultado.message || 'Cliente y vehículo creados exitosamente'
      };
    }
    
    // Si llegamos aquí, hubo un error en la respuesta
    const errorMessage = response?.data?.message || 'Error al crear cliente y vehículo';
    throw new Error(errorMessage);
    
  } catch (error) {
    console.error('❌ [PatenteService] Error creando cliente y vehículo:', error);
    
    // Manejar errores específicos
    let errorMessage = 'Error al crear cliente y vehículo';
    
    if (error.message.includes('RUT')) {
      errorMessage = 'Ya existe un cliente con este RUT';
    } else if (error.message.includes('patente')) {
      errorMessage = 'Ya existe un vehículo con esta patente';
    } else if (error.status >= 400 && error.status < 500) {
      errorMessage = 'Datos inválidos. Verifica la información ingresada.';
    } else if (error.status >= 500) {
      errorMessage = 'Error del servidor. Intenta nuevamente.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      message: errorMessage
    };
  }
};

/**
 * Obtiene estadísticas del servicio de patentes
 * @returns {Promise<Object>} - Estadísticas del servicio
 */
export const obtenerEstadisticas = async () => {
  try {
    //console.log('📊 [PatenteService] Obteniendo estadísticas');
    
    const response = await apiHelpers.generic.get(API_ENDPOINTS.PATENTES.ESTADISTICAS, 'estadisticas');
    
    if (response?.data?.success) {
      return {
        success: true,
        stats: response.data.stats
      };
    }
    
    throw new Error('Error al obtener estadísticas');
    
  } catch (error) {
    console.error('❌ [PatenteService] Error obteniendo estadísticas:', error);
    throw error;
  }
};

/**
 * Limpia el cache de patentes (solo para testing/admin)
 * @param {Object} options - Opciones de limpieza
 * @param {boolean} options.onlyEmpty - Solo limpiar entradas vacías/inválidas
 * @param {string} options.patente - Limpiar una patente específica
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const limpiarCache = async (options = {}) => {
  try {
    const { onlyEmpty = false, patente = null } = options;
    
    //console.log('🗑️ [PatenteService] Limpiando cache', { onlyEmpty, patente });
    
    const requestBody = {
      onlyEmpty,
      ...(patente && { patente })
    };
    
    const response = await apiHelpers.generic.post(API_ENDPOINTS.PATENTES.LIMPIAR_CACHE, requestBody, 'generic');
    
    if (response?.data?.success) {
      return {
        success: true,
        message: response.data.message || 'Cache limpiado exitosamente',
        removedCount: response.data.removedCount,
        found: response.data.found
      };
    }
    
    throw new Error('Error al limpiar cache');
    
  } catch (error) {
    console.error('❌ [PatenteService] Error limpiando cache:', error);
    throw error;
  }
};